// SPDX-License-Identifier: AGPL-3.0-or-later
import type { Dataset, Division } from '../types';
import { num, pct, delta, esc, COL } from '../format';
import { gloss } from '../glossary';
import { tipAttr } from '../charts';
import { squarify } from '../utils/squarify';

const GOLD_RAMP = ['#f6ecda', '#eed6a6', '#ddb35f', '#c9922f', '#a9761f'];
const goldFor = (share: number | null): string => {
  if (share == null) return '#e9e9e2';
  const th = [0.3, 0.4, 0.5, 0.6];
  let i = 0; while (i < th.length && share >= th[i]) i++;
  return GOLD_RAMP[i];
};

export function renderOffences(root: HTMLElement, data: Dataset): void {
  const divs = data.offences.divisions.filter((d) => d.numberNow > 0);

  root.innerHTML = `
    <div class="view-head">
      <h2>What people are in prison for</h2>
      <p>Each prisoner is counted under a single ${gloss('mso', 'most serious offence/charge')}, grouped into the 16 ${gloss('anzsoc', 'ANZSOC')} divisions. Acts intended to cause injury (assault) is now the largest — and, like several others, most of its prisoners are on ${gloss('remand', 'remand')} rather than sentenced.</p>
    </div>

    <div class="panel">
      <div class="panel-head"><h3>Prisoners by most serious offence, ${data.meta.latestYear}</h3><p>Rectangle area is the number of prisoners; colour is the share on remand (unsentenced).</p></div>
      <div class="chart-wrap" id="off-tree"></div>
      <div class="map-legend"><span class="legend-item">less on remand</span><span class="ramp">${GOLD_RAMP.map((c) => `<span style="background:${c}"></span>`).join('')}</span><span class="legend-item">more on remand</span></div>
    </div>

    <div class="two-col" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-head"><h3>Sentenced vs on remand, by offence</h3><p>Which offences skew to unsentenced prisoners awaiting the outcome of their case.</p></div>
        <div id="off-split"></div>
      </div>
      <div class="panel">
        <div class="panel-head"><h3>What has changed since ${data.meta.firstYear}</h3><p>Change in prisoner numbers by offence.</p></div>
        <div id="off-change"></div>
      </div>
    </div>
  `;

  drawTree(root.querySelector('#off-tree') as HTMLElement, divs);
  drawSplit(root.querySelector('#off-split') as HTMLElement, divs);
  drawChange(root.querySelector('#off-change') as HTMLElement, data);
}

function drawTree(el: HTMLElement, divs: Division[]): void {
  const W = 900, H = 420;
  const rects = squarify(divs.map((d) => d.numberNow), W, H);
  let cells = '';
  rects.forEach((r, i) => {
    const d = divs[i];
    const big = r.w > 92 && r.h > 34;
    const tip = `${d.name}\n${num(d.numberNow)} prisoners (${pct(d.proportionNow)})\n${d.remandShare == null ? '' : `${pct(d.remandShare)} on remand`}`;
    cells += `<g ${tipAttr(tip)} aria-label="${esc(d.name)}: ${num(d.numberNow)}">
      <rect class="cell" x="${r.x.toFixed(1)}" y="${r.y.toFixed(1)}" width="${r.w.toFixed(1)}" height="${r.h.toFixed(1)}" fill="${goldFor(d.remandShare)}" stroke="#fff" stroke-width="1.5"/>
      ${big ? `<text x="${(r.x + 7).toFixed(1)}" y="${(r.y + 17).toFixed(1)}" style="font-size:12px;font-weight:600;fill:#3a2c14">${esc(d.name.length > 26 ? d.name.slice(0, 24) + '…' : d.name)}</text>
      <text x="${(r.x + 7).toFixed(1)}" y="${(r.y + 33).toFixed(1)}" class="num" style="font-size:11px;fill:#5a4a2a">${num(d.numberNow)}</text>` : ''}
    </g>`;
  });
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Treemap of prisoners by offence">${cells}</svg>`;
}

function drawSplit(el: HTMLElement, divs: Division[]): void {
  const rows = divs.filter((d) => d.remandShare != null).slice().sort((a, b) => (b.remandShare as number) - (a.remandShare as number));
  el.className = 'barlist';
  el.innerHTML = rows.map((d) => {
    const remand = (d.remandShare as number) * 100;
    return `<div class="barrow" style="cursor:default" ${tipAttr(`${d.name}\nSentenced ${num(d.sentenced)} · Remand ${num(d.unsentenced)}\n${pct(d.remandShare)} on remand`)} aria-label="${esc(d.name)}: ${pct(d.remandShare)} on remand">
      <span class="lbl">${esc(d.name)}</span>
      <span class="track"><span style="width:${(100 - remand).toFixed(1)}%;background:${COL.sentenced};float:left"></span><span style="width:${remand.toFixed(1)}%;background:${COL.remand};float:left"></span></span>
      <span class="val">${remand.toFixed(0)}%</span>
    </div>`;
  }).join('') + `<div class="legend" style="margin-top:8px"><span class="legend-item"><span class="legend-swatch" style="background:${COL.sentenced}"></span>Sentenced</span><span class="legend-item"><span class="legend-swatch" style="background:${COL.remand}"></span>Remand</span></div>`;
}

function drawChange(el: HTMLElement, data: Dataset): void {
  const rows = data.offences.divisions
    .filter((d) => d.changePct != null)
    .slice()
    .sort((a, b) => Math.abs(b.changePct as number) - Math.abs(a.changePct as number))
    .slice(0, 10);
  const maxAbs = Math.max(...rows.map((d) => Math.abs(d.changePct as number)), 0.01);
  el.className = 'barlist';
  el.innerHTML = rows.map((d) => {
    const v = d.changePct as number;
    const w = (Math.abs(v) / maxAbs) * 100;
    const col = v >= 0 ? 'var(--status-bad)' : 'var(--status-good)';
    return `<div class="barrow" style="cursor:default;grid-template-columns:minmax(0,190px) 1fr auto" ${tipAttr(`${d.name}\n${num(d.number[0])} (${data.meta.firstYear}) → ${num(d.numberNow)} (${data.meta.latestYear})\n${delta(v)}`)} aria-label="${esc(d.name)}: ${delta(v)}">
      <span class="lbl">${esc(d.name)}</span>
      <span class="track"><span style="width:${w.toFixed(1)}%;background:${col}"></span></span>
      <span class="val" style="color:${col}">${delta(v)}</span>
    </div>`;
  }).join('');
}
